import React, { useEffect, useState } from 'react';
import { Search, Clock, Star, Filter } from 'lucide-react';
import { servicesAPI } from '../../services/api';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';

const Services = () => {
  const [services, setServices] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = ['All', 'Hair', 'Nails', 'Spa', 'Makeup'];

  useEffect(() => {
    fetchServices();
  }, []);

  useEffect(() => {
    filterServices();
  }, [services, search, selectedCategory]);

  const fetchServices = async () => {
    try {
      const response = await servicesAPI.getAll();
      setServices(response.data.services || []);
    } catch (error) {
      console.error('Failed to fetch services:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterServices = () => {
    let filtered = services;
    
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(s => s.category === selectedCategory);
    }
    
    if (search) {
      filtered = filtered.filter(s => 
        s.name.toLowerCase().includes(search.toLowerCase()) ||
        s.description.toLowerCase().includes(search.toLowerCase())
      );
    }
    
    setFilteredServices(filtered);
  };

  if (loading) {
    return (
      <div className="p-4 md:p-6 space-y-6">
        <div className="skeleton h-12 rounded-xl" />
        <div className="flex gap-2 overflow-x-auto">
          {[1,2,3,4,5].map(i => <div key={i} className="skeleton h-10 w-20 rounded-full flex-shrink-0" />)}
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="skeleton h-64 rounded-2xl" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6 animate-fadeIn">
      {/* Header */}
      <div>
        <h1 className="font-serif text-3xl text-[#4A154B] mb-2">Our Services</h1>
        <p className="text-gray-600">Earn 10 points for every $1 spent</p>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <Input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search services..."
          className="pl-12 h-12"
          data-testid="services-search-input"
        />
      </div>

      {/* Categories */}
      <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4">
        {categories.map((cat) => (
          <button
            key={cat}
            onClick={() => setSelectedCategory(cat)}
            className={`px-5 py-2.5 rounded-full font-medium text-sm whitespace-nowrap transition-all ${
              selectedCategory === cat
                ? 'bg-[#4A154B] text-white'
                : 'bg-[#F5E6D3] text-[#4A154B] hover:bg-[#C9A689] hover:text-white'
            }`}
            data-testid={`category-${cat.toLowerCase()}`}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Services Grid */}
      <div className="grid md:grid-cols-2 gap-4">
        {filteredServices.map((service) => (
          <div
            key={service.id}
            className="service-card"
            data-testid={`service-card-${service.id}`}
          >
            <div className="relative">
              <img
                src={service.image_url || `https://images.unsplash.com/photo-1560066984-138dadb4c035?w=400`}
                alt={service.name}
                className="service-card-image"
              />
              {service.is_featured && (
                <Badge className="absolute top-3 right-3 bg-[#C9A689] text-white">
                  <Star className="w-3 h-3 mr-1" />
                  Featured
                </Badge>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
              <div className="absolute bottom-3 left-3">
                <Badge variant="secondary" className="bg-white/90 text-[#4A154B]">
                  {service.category}
                </Badge>
              </div>
            </div>
            
            <div className="p-5">
              <div className="flex items-start justify-between mb-2">
                <h3 className="font-serif text-lg text-[#4A154B]">{service.name}</h3>
                <span className="font-semibold text-[#C9A689] text-lg">${service.price}</span>
              </div>
              
              <p className="text-sm text-gray-600 mb-4 line-clamp-2">{service.description}</p>
              
              <div className="flex items-center justify-between pt-3 border-t border-[#F5E6D3]">
                <div className="flex items-center gap-1 text-gray-500 text-sm">
                  <Clock className="w-4 h-4" />
                  <span>{service.duration} min</span>
                </div>
                <div className="flex items-center gap-1 text-[#C9A689] text-sm font-medium">
                  <Star className="w-4 h-4" />
                  <span>Earn {service.price * 10} pts</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredServices.length === 0 && (
        <div className="text-center py-12">
          <Filter className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No services found matching your criteria</p>
        </div>
      )}
    </div>
  );
};

export default Services;
